/**
 * lib/entranceAssessment.ts
 * ------------------------------------------------------------------
 * The entrance test, and whether an application may be approved without it.
 *
 * PURE. Marks in, an answer out. No Firestore — whether a child may be
 * offered a place is a decision the school makes, and the arithmetic behind
 * it should be readable and checkable on its own.
 *
 * WHY IT EXISTS. The school runs an entrance test and the printed form has
 * always had a box for it — "Academics Office: entrance exam date, passed
 * YES / NO" — filled in by hand and then typed nowhere. So the portal could
 * approve an application that had never been assessed, and nothing in the
 * record said whether the child had sat the test at all. The paper knew and
 * the system did not.
 *
 * IT GATES APPROVAL, NOT PRINTING. The form is printed in order to RUN the
 * test — it is the sheet the marks are written on — so refusing to print it
 * until the marks exist would be a circle. Documents are different and gate
 * both: the office already holds those before anything is printed.
 *
 * A FAIL DOES NOT BLOCK AN APPROVAL. Recording the outcome is required;
 * overriding it is the school's business. A system that refuses to admit a
 * child the head has decided to admit will be worked around, and the
 * workaround is what stops being recorded. What it does instead is make the
 * override VISIBLE: approving against a fail is stored as exactly that.
 * ------------------------------------------------------------------ */

export type EntranceOutcome = 'pass' | 'fail' | 'exempt';

export interface EntranceMark {
  /** Free text — the school sets its own papers and renames them. */
  subject: string;
  /** Marks awarded. */
  score: number;
  /** Marks available. Per subject, because papers are not all out of 100. */
  outOf: number;
}

export interface EntranceAssessment {
  /** The day the child sat it. */
  sittingDate: string;
  marks: EntranceMark[];
  /**
   * The school's verdict, set by a person.
   *
   * NOT DERIVED FROM A PASS MARK. A school admits on more than an average —
   * a strong mathematician who reads poorly may still be taken — and a
   * threshold in code would either be wrong or be ignored. The percentage
   * is calculated and shown; the decision stays human.
   */
  outcome: EntranceOutcome;
  /** Why, particularly when the outcome disagrees with the marks. */
  notes?: string;
  recordedBy?: string;
  recordedByName?: string;
  recordedAt?: string;
}

/** The papers LKC usually sets. Suggestions the office can change. */
export const SUGGESTED_SUBJECTS = ['English', 'Mathematics', 'Science'] as const;

export const OUTCOME_LABEL: Record<EntranceOutcome, string> = {
  pass: 'Passed',
  fail: 'Did not pass',
  exempt: 'Exempted from the test',
};

/* ----------------------------- the marks ----------------------------- */

/** Why this mark cannot be recorded, or null if it can. */
export function markProblem(m: Partial<EntranceMark>): string | null {
  if (!String(m.subject ?? '').trim()) return 'Name the paper.';
  const outOf = Number(m.outOf);
  const score = Number(m.score);
  if (!Number.isFinite(outOf) || outOf <= 0) return 'Say what the paper was out of.';
  if (!Number.isFinite(score) || score < 0) return 'A mark cannot be negative.';
  // The classic transcription slip, and the one that quietly inflates an
  // average: 85 typed into a paper that was out of 50.
  if (score > outOf) return `${score} is more than the paper is out of (${outOf}).`;
  return null;
}

/** Total percentage across every paper, weighted by what each was out of. */
export function overallPercent(marks: EntranceMark[]): number | null {
  const usable = marks.filter((m) => !markProblem(m));
  if (!usable.length) return null;
  const total = usable.reduce((a, m) => a + Number(m.outOf), 0);
  if (total <= 0) return null;
  const scored = usable.reduce((a, m) => a + Number(m.score), 0);
  return Math.round((scored / total) * 1000) / 10;
}

/** One paper as a percentage, for the row the office is looking at. */
export function markPercent(m: EntranceMark): number | null {
  if (markProblem(m)) return null;
  return Math.round((Number(m.score) / Number(m.outOf)) * 1000) / 10;
}

/* ------------------------------ the gate ------------------------------ */

/**
 * Why this application cannot be approved yet, or null if it can.
 *
 * An EXEMPT assessment needs no marks — a transferring pupil with a full
 * set of results from another school may be excused the test, and requiring
 * marks that were never sat would only teach the office to invent them.
 */
export function assessmentProblem(a: EntranceAssessment | null | undefined): string | null {
  if (!a) return 'The entrance assessment has not been recorded.';
  if (!a.sittingDate) return 'Give the date the entrance test was sat.';
  if (!a.outcome) return 'Record whether the applicant passed.';
  if (a.outcome === 'exempt') {
    return String(a.notes ?? '').trim()
      ? null
      : 'Say why this applicant is exempt from the entrance test.';
  }
  const marks = a.marks ?? [];
  if (!marks.length) return 'Enter the entrance test marks.';
  const bad = marks.map(markProblem).find(Boolean);
  if (bad) return bad;
  return null;
}

/** Is the office approving against the test's own verdict? */
export function isOverridingFail(a: EntranceAssessment | null | undefined): boolean {
  return a?.outcome === 'fail';
}

/**
 * A one-line summary for the printed form and the application list.
 *
 * The paper form has a box for exactly this and it was always filled in by
 * hand. Now it can be printed already answered.
 */
export function summarise(a: EntranceAssessment | null | undefined): string {
  if (!a) return 'Not assessed';
  if (a.outcome === 'exempt') return `Exempt — ${a.sittingDate || 'no date'}`;
  const pct = overallPercent(a.marks ?? []);
  const verdict = OUTCOME_LABEL[a.outcome] ?? a.outcome;
  return pct === null
    ? `${verdict} — ${a.sittingDate || 'no date'}`
    : `${verdict}, ${pct}% — ${a.sittingDate || 'no date'}`;
}
